/**
 * Режим обслуживания стенда: API отвечает 503 с `code: SERVICE_MAINTENANCE`, вкладка уходит на страницу обслуживания
 * (её отдаёт Front по `MAINTENANCE_PATH` на том же origin) и вернётся по `returnUrl`. Фоновая вкладка не уходит сразу:
 * при открытии она сверяется со статусом стенда (`SERVICE_STATUS_URL`) — обслуживание могло уже закончиться.
 */

export const MAINTENANCE_PATH = '/maintenance';
export const SERVICE_STATUS_URL = '/service-status.json';

export interface IMaintenanceRedirectOptions {
    /** Оформление страницы обслуживания (`surface=admin` — админка). */
    surface?: string;
    /** Путь страницы обслуживания в самом приложении: вкладка уже на ней — переход не нужен (Front). */
    selfPath?: string;
}

/** Окно глазами перехода — шов тестов клиента; по умолчанию — глобальные `window.location`, `document`, `fetch`. */
export interface IMaintenanceEnvironment {
    location?: Pick<Location, 'pathname' | 'search' | 'hash' | 'origin' | 'replace'>;
    document?: {
        readonly hidden: boolean;
        addEventListener(type: 'visibilitychange', listener: () => void): void;
        removeEventListener(type: 'visibilitychange', listener: () => void): void;
    };
    fetch?: (input: string, init?: RequestInit) => Promise<Response>;
}

/**
 * Переход на страницу обслуживания — один на вкладку. Экземпляр держит модуль приложения; создание ничего не делает:
 * подписка на `visibilitychange` — только в фоновой вкладке до проверки статуса.
 */
export function createMaintenanceRedirect(
    { surface, selfPath }: IMaintenanceRedirectOptions = {},
    env: IMaintenanceEnvironment = {},
): () => void {
    let redirectStarted = false;
    let pendingVisibilityCheck = false;
    let checkInProgress = false;

    // Глобальные объекты — в момент вызова: тесты подменяют их после создания экземпляра.
    const location = () => env.location ?? window.location;
    const page = () => env.document ?? document;
    const onSelfPage = () => !!selfPath && location().pathname === selfPath;

    const onVisibilityChange = () => {
        void checkWhenVisible();
    };

    const stopWaiting = () => {
        pendingVisibilityCheck = false;
        page().removeEventListener('visibilitychange', onVisibilityChange);
    };

    const navigate = () => {
        if (redirectStarted || onSelfPage()) return;
        stopWaiting();
        redirectStarted = true;
        const { pathname, search, hash, origin } = location();
        const target = new URL(MAINTENANCE_PATH, origin);
        if (surface) target.searchParams.set('surface', surface);
        target.searchParams.set('returnUrl', pathname + search + hash);
        location().replace(target.pathname + target.search);
    };

    const checkWhenVisible = async () => {
        if (page().hidden || !pendingVisibilityCheck || checkInProgress) return;
        checkInProgress = true;
        try {
            const response = await (env.fetch ?? fetch)(SERVICE_STATUS_URL, { cache: 'no-store' });
            if (!response.ok) return;
            const data: unknown = await response.json();
            const state = data && typeof data === 'object' && 'state' in data ? data.state : undefined;
            if (state === 'offline') {
                if (!page().hidden) navigate();
            } else if (state === 'normal' || state === 'completed' || state === 'cancelled') {
                stopWaiting();
            }
        } catch {
            // Проверим снова при следующем открытии вкладки или ответе API.
        } finally {
            checkInProgress = false;
        }
    };

    return () => {
        if (redirectStarted || onSelfPage()) return;
        if (!page().hidden) {
            navigate();
            return;
        }
        if (!pendingVisibilityCheck) {
            pendingVisibilityCheck = true;
            page().addEventListener('visibilitychange', onVisibilityChange);
        }
    };
}

/** Тело ответа 503 — признак обслуживания (`code: SERVICE_MAINTENANCE`): JSON, строка, Blob или ArrayBuffer. */
export const isServiceMaintenanceResponse = async (data: unknown): Promise<boolean> => {
    if (data instanceof Blob) {
        try {
            data = await data.text();
        } catch {
            return false;
        }
    }
    if (data instanceof ArrayBuffer) data = new TextDecoder().decode(data);
    if (typeof data === 'string') {
        try {
            data = JSON.parse(data);
        } catch {
            return false;
        }
    }
    return typeof data === 'object' && data !== null && 'code' in data && data.code === 'SERVICE_MAINTENANCE';
};
